<script setup lang="ts">
import type { Query } from '@directus/types'

definePageMeta({
  title: 'Customer Stories',
  description: 'Stories of ICS solutions delivered to satisfied customers.',
  order: 0,
})

const route = useRoute()

const query: Query = {
  fields: [
    '*',
    'featured_image.id',
    'featured_image.title',
    'featured_image.description',
  ],
  filter: {
    status: { _eq: 'published' },
  },
}

const { data: stories } = await useFetch<(Story & { featured_image: Pick<DirectusFile, 'id' | 'title' | 'description'> })[]>(
  '/api/items/stories',
  {
    key: 'page:stories:all-stories',
    query,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  },
)

const atStoriesRoot = computed(() => /\/about\/stories\/?$/.test(route.path))
</script>

<template>
  <BlockContent
    element="section"
    class="dark:text-icssilver-50"
  >
    <h1 class="subpage-title mb-12">
      Success Stories from Our Customers
    </h1>
    <template v-if="atStoriesRoot">
      <div class="prose sm:text-lg">
        <p>
          Peruse examples of ICS solutions delivered to satisfied customers.
        </p>
        <p class="mb-12">
          Interested in what ICS can do for you? <NuxtLink
            to="/contact"
            class="link inline-block"
          >
            Let's start a conversation.
          </NuxtLink>
        </p>
      </div>
      <div class="flex flex-wrap gap-y-12 f-gap-x-48-64">
        <template
          v-for="story in stories"
          :key="story.id"
        >
          <div class="gap-6 grid auto-rows-min grid-cols-2">
            <h3 class="text-lg col-span-2 heading">
              {{ story.title }}
            </h3>
            <figure class="col-span-2 space-y-2 md:col-span-1 md:order-last">
              <NuxtImg
                :src="story.featured_image.id"
                :alt="`${story.featured_image.title}: ${story.featured_image.description}`"
              />
              <figcaption class="text-icsgray-800 italic dark:text-icsgray-200">
                {{ story.featured_image.description }}
              </figcaption>
            </figure>
            <div
              class="col-span-2 prose md:col-span-1"
              v-html="story.content"
            />
          </div>
        </template>
      </div>
    </template>
    <NuxtPage />
  </BlockContent>
</template>
